// Tela de Parceiros na Visão Geral (#6498): parceiros comerciais e vendedores parceiros.
//
// Pergunta da tela: como estão os clientes que eu trouxe, e quanto vou receber?
//   - pendências e funil: as negociações de hoje da pessoa e, para o parceiro, também
//     as dos vendedores parceiros da rede dele (`profiles.parceiro_id`);
//   - aprovados e comissão: a conta do "Meu Trabalho" do CRM (aprovação no mês, regra
//     da Folha: 1ª parcela de 30% depois da aprovação, 2ª de 70% depois da 1ª fatura).
//   - o link próprio para captar clientes (#6511), logo no topo: é a ferramenta do dia.
import React, { useCallback, useMemo } from 'react';
import { LinkCaptacaoParceiro } from '../../../src/components/LinkCaptacaoParceiro';
import { useNavigate } from 'react-router-dom';
import { Gift, KanbanSquare, Plus } from 'lucide-react';
import { useResultadoMes } from '../../CRM/Kanban/useResultadoMes';
import { isGrupoTarifarioA } from '../../../src/lib/comissaoUtils';
import { formatarValorKpi } from '../../../src/lib/dashboard/kpisDepartamento';
import { rotuloDoMes } from '../../../src/lib/dashboard/metasMensais';
import { lerMes } from '../../../src/lib/visaoGeral/periodo';
import { diasDesde } from '../../../src/lib/visaoGeral/comercial';
import {
  ETAPAS_CLIENTE, comissaoPorCliente, funilDosClientes, pendenciasDosClientes,
} from '../../../src/lib/visaoGeral/parceiros';
import { BlocosDoDia } from '../BlocosDoDia';
import { BotaoAcao, CabecalhoVisaoGeral, CartaoHoje, CartaoIndicador, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral, plural } from '../componentes';
import { DIAS_TERMO_SEM_ASSINATURA, useParametrosComissao } from '../useGestaoComercial';
import { useClientesDoParceiro, useRedeDoParceiro } from '../useParceiro';
import type { PropsTelaVisaoGeral } from '../tipos';

const kwh = (v: number | null | undefined) => formatarValorKpi(v ?? null, 'kwh');
const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const PENDENCIAS_NA_LISTA = 8;

const TelaParceiros: React.FC<PropsTelaVisaoGeral> = ({ pessoa, souEu, definicao, periodo, aoMudarMes }) => {
  const navigate = useNavigate();
  const { mes, ano } = useMemo(() => {
    const { ano, mes0 } = lerMes(periodo.mes)!;
    return { mes: mes0 + 1, ano };
  }, [periodo.mes]);
  const ehVendedorParceiro = pessoa.role === 'vendedor_parceiro';

  // ── dados ──
  const rede = useRedeDoParceiro(pessoa);
  const pessoas = useMemo(() => (rede ? [pessoa, ...rede.equipe] : null), [pessoa, rede]);
  const ids = useMemo(() => pessoas?.map(p => p.id) ?? null, [pessoas]);
  const nomePorId = useMemo(() => new Map((pessoas ?? []).map(p => [p.id, p.full_name || 'Sem nome'])), [pessoas]);
  const { clientes, erro } = useClientesDoParceiro(ids);
  const parametros = useParametrosComissao(useMemo(() => [pessoa.id], [pessoa.id]), mes, ano);

  const semEscopo = useCallback(async () => null, []);
  const todosVisiveis = useCallback(() => true, []);
  const resultado = useResultadoMes({
    selecionados: ids ?? [pessoa.id], escopoResponsaveis: semEscopo, perfis: pessoas ?? [pessoa],
    responsavelVisivel: todosVisiveis, ativo: ids !== null, mes: periodo.mes,
  });

  const funil = useMemo(() => (clientes ? funilDosClientes(clientes) : null), [clientes]);
  const pendencias = useMemo(() => (clientes ? pendenciasDosClientes(clientes) : null), [clientes]);
  const nomeCliente = useMemo(() => new Map((clientes ?? []).map(c => [c.id, c.cliente_nome])), [clientes]);
  const agora = new Date();
  const assinaturas = pendencias?.filter(p => p.tipo === 'assinatura') ?? null;
  const documentos = pendencias?.filter(p => p.tipo === 'documento') ?? null;
  const assinaturasParadas = assinaturas?.filter(p => diasDesde(p.desde, agora) >= DIAS_TERMO_SEM_ASSINATURA).length ?? 0;

  // Aprovados no mês (base da comissão), da pessoa e da rede.
  const aprovados = useMemo(() => {
    if (resultado.atualizadoEm == null || !parametros) return null;
    const linhas = resultado.pessoas.flatMap(p => p.aprovadas.map(a => ({
      id: Number(a.id),
      kwh: Number(a.consumo_medio_kwh) || 0,
      grupoA: isGrupoTarifarioA(a.unidades_consumidoras?.[0]?.grupo_tarifario),
      responsavelId: p.responsavelId,
    })));
    const comComissao = comissaoPorCliente(linhas, parametros.tarifa);
    return comComissao.map((c, i) => ({ ...c, responsavelId: linhas[i].responsavelId })).sort((a, b) => b.kwh - a.kwh);
  }, [resultado.pessoas, resultado.atualizadoEm, parametros]);
  const totalAprovado = aprovados?.reduce((t, a) => t + a.kwh, 0) ?? null;
  const parcela1 = aprovados?.reduce((t, a) => t + a.parcela1, 0) ?? null;
  const parcela2 = aprovados?.reduce((t, a) => t + a.parcela2, 0) ?? null;
  const grupoA = aprovados?.filter(a => a.grupoA).length ?? 0;

  const temEquipe = (rede?.equipe.length ?? 0) > 0;
  const emAndamento = funil ? funil.contato.qtd + funil.simulacao.qtd + funil.assinatura.qtd + funil.analise.qtd + funil.pendencia.qtd : null;
  const paraQuem = ehVendedorParceiro && rede?.dono ? ` A comissão é paga ao seu parceiro, ${rede.dono.full_name}.` : '';
  const semTarifa = parametros && parametros.tarifa == null;

  const acoes = souEu ? <BotaoAcao rotulo="Nova negociação" icone={Plus} para="/crm?novaNegociacao=1" /> : null;

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} aoMudarMes={aoMudarMes} acoes={acoes} />

      <LinkCaptacaoParceiro codigo={pessoa.codigo_indicacao} souEu={souEu} />

      <SecaoVisaoGeral titulo="Hoje" subtitulo={periodo.corrente ? 'o que depende dos seus clientes agora' : 'sempre a situação de agora, qualquer que seja o mês'} colunas={2}>
        <CartaoHoje
          valor={assinaturas ? assinaturas.length : null}
          rotulo="termos esperando a assinatura do cliente"
          tom={assinaturasParadas > 0 ? 'atencao' : 'neutro'}
          para="/crm"
          dica={assinaturas?.length ? `${assinaturasParadas} enviados há ${DIAS_TERMO_SEM_ASSINATURA} dias ou mais (contando o último envio).` : undefined}
        />
        <CartaoHoje
          valor={documentos ? documentos.length : null}
          rotulo="clientes com documento pendente"
          tom="critico"
          para="/crm"
          dica="A validação de dados pediu documento: sem ele, o cliente não é aprovado."
        />
      </SecaoVisaoGeral>
      {erro && <p className="-mt-6 text-xs text-red-600 dark:text-red-400">{erro}</p>}

      {pendencias && pendencias.length > 0 && (
        <PainelDetalhe titulo="Clientes com pendência" acao={{ rotulo: 'Abrir o CRM', para: '/crm' }}>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {pendencias.slice(0, PENDENCIAS_NA_LISTA).map(p => {
              const dias = diasDesde(p.desde, agora);
              const atrasada = p.tipo === 'documento' || dias >= DIAS_TERMO_SEM_ASSINATURA;
              return (
                <li key={p.negociacaoId}>
                  <button type="button" onClick={() => navigate(`/crm?negociacaoId=${p.negociacaoId}`)}
                    className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2">
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{p.cliente || `Negociação #${p.negociacaoId}`}</span>
                      <span className="block text-xs text-gray-400">
                        {p.tipo === 'assinatura' ? 'Assinar o termo' : 'Enviar o documento que a análise pediu'}
                        {temEquipe && p.responsavelId !== pessoa.id ? ` · ${nomePorId.get(p.responsavelId ?? '') ?? ''}` : ''}
                      </span>
                    </span>
                    <span className={`text-xs whitespace-nowrap ${atrasada ? 'text-amber-700 dark:text-amber-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                      {p.desde ? (dias === 0 ? 'hoje' : `há ${plural(dias, 'dia', 'dias')}`) : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {pendencias.length > PENDENCIAS_NA_LISTA && (
            <p className="text-xs text-gray-400 mt-3">Mais {plural(pendencias.length - PENDENCIAS_NA_LISTA, 'cliente', 'clientes')} no CRM.</p>
          )}
        </PainelDetalhe>
      )}

      <BlocosDoDia pessoa={pessoa} perfil="parceiros" />

      <SecaoVisaoGeral titulo="Meu mês" subtitulo={periodo.rotulo}>
        <CartaoIndicador
          titulo={temEquipe ? 'Clientes no funil (com a equipe)' : 'Meus clientes no funil'}
          valor={emAndamento == null ? null : emAndamento.toLocaleString('pt-BR')}
          composicao={funil ? ETAPAS_CLIENTE.map(e => ({
            rotulo: e.rotulo, valor: `${funil[e.id].qtd.toLocaleString('pt-BR')} · ${kwh(funil[e.id].kwh)}`,
          })) : undefined}
          detalhe="Situação de hoje das negociações em aberto. Perdidas e reprovadas ficam de fora."
          para="/crm"
        />
        <CartaoIndicador
          titulo="Aprovados no mês"
          valor={totalAprovado == null ? null : kwh(totalAprovado)}
          detalhe={aprovados ? `${plural(aprovados.length, 'cliente aprovado', 'clientes aprovados')} em ${rotuloDoMes(periodo.mes)}.` : undefined}
          origem="Aprovação da validação de dados no mês: a mesma base da comissão."
        />
        <CartaoIndicador
          titulo={ehVendedorParceiro ? 'Comissão gerada: 1ª parcela' : '1ª parcela garantida'}
          valor={parcela1 == null ? null : semTarifa ? '—' : reais(parcela1)}
          detalhe={semTarifa ? 'Falta a tarifa do mês para calcular.'
            : `30% da comissão das aprovadas no mês, paga depois da aprovação.${grupoA ? ` ${plural(grupoA, 'cliente do Grupo A fica', 'clientes do Grupo A ficam')} de fora (o gerente calcula à parte).` : ''}${paraQuem}`}
          origem="Regra da Folha de Pagamento: faixa da comissão pelo kWh de cada cliente × tarifa do mês."
        />
        <CartaoIndicador
          titulo={ehVendedorParceiro ? 'Comissão gerada: 2ª parcela' : '2ª parcela prevista'}
          valor={parcela2 == null ? null : semTarifa ? '—' : reais(parcela2)}
          detalhe={semTarifa ? undefined : `70% da comissão, paga depois da 1ª fatura de cada cliente.${paraQuem}`}
        />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>
        <PainelDetalhe titulo={`Aprovados em ${rotuloDoMes(periodo.mes)}`}>
          {aprovados === null ? (
            <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : aprovados.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum cliente aprovado em {rotuloDoMes(periodo.mes)} ainda.</p>
          ) : (
            <div className="-mx-5 overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-gray-400">
                    <th className="px-5 py-2 text-left font-semibold">Cliente</th>
                    {temEquipe && <th className="px-3 py-2 text-left font-semibold">Quem vendeu</th>}
                    <th className="px-3 py-2 text-right font-semibold">kWh</th>
                    <th className="px-3 py-2 text-right font-semibold">1ª parcela</th>
                    <th className="px-5 py-2 text-right font-semibold">2ª parcela</th>
                  </tr>
                </thead>
                <tbody>
                  {aprovados.map(a => (
                    <tr key={a.id} className="border-t border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-200">
                      <td className="px-5 py-2">
                        <button type="button" onClick={() => navigate(`/crm?negociacaoId=${a.id}`)} className="text-left hover:underline">
                          {nomeCliente.get(a.id) || `Negociação #${a.id}`}
                        </button>
                      </td>
                      {temEquipe && <td className="px-3 py-2 whitespace-nowrap text-gray-500">{nomePorId.get(a.responsavelId) ?? ''}</td>}
                      <td className="px-3 py-2 text-right tabular-nums">{kwh(a.kwh)}</td>
                      {a.grupoA ? (
                        <td colSpan={2} className="px-5 py-2 text-right text-xs text-gray-400">Grupo A: o gerente calcula à parte</td>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-right tabular-nums">{semTarifa ? '—' : reais(a.parcela1)}</td>
                          <td className="px-5 py-2 text-right tabular-nums text-gray-500">{semTarifa ? '—' : reais(a.parcela2)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">
            Valores previstos pela regra da Folha de Pagamento; o pagamento é confirmado pelo Financeiro. A 2ª parcela depende da 1ª fatura de cada cliente.
          </p>
        </PainelDetalhe>

        {temEquipe && (
          <PainelDetalhe titulo="Minha equipe">
            <div className="-mx-5 overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-gray-400">
                    <th className="px-5 py-2 text-left font-semibold">Vendedor parceiro</th>
                    <th className="px-3 py-2 text-right font-semibold">No funil</th>
                    <th className="px-3 py-2 text-right font-semibold">Termos para assinar</th>
                    <th className="px-3 py-2 text-right font-semibold">Aprovados no mês</th>
                    <th className="px-5 py-2 text-right font-semibold">kWh aprovado</th>
                  </tr>
                </thead>
                <tbody>
                  {rede!.equipe.map(v => {
                    const dele = (clientes ?? []).filter(c => c.responsavel_id === v.id);
                    const f = funilDosClientes(dele);
                    const apr = (aprovados ?? []).filter(a => a.responsavelId === v.id);
                    return (
                      <tr key={v.id} className="border-t border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-200">
                        <td className="px-5 py-2 whitespace-nowrap">{v.full_name}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{f.contato.qtd + f.simulacao.qtd + f.assinatura.qtd + f.analise.qtd + f.pendencia.qtd}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{f.assinatura.qtd}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{apr.length}</td>
                        <td className="px-5 py-2 text-right tabular-nums">{kwh(apr.reduce((t, a) => t + a.kwh, 0))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-3">As vendas da equipe geram comissão para você, pela mesma regra.</p>
          </PainelDetalhe>
        )}
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'CRM', para: '/crm', icone: KanbanSquare },
        { rotulo: 'Indicações', para: '/crm/indicacoes', icone: Gift },
      ]} />
    </div>
  );
};

export default TelaParceiros;
